import React from 'react';
import { motion } from 'framer-motion';
import {
    Wind,
    Recycle,
    Flame,
    Car,
    Battery,
    Server,
    Leaf,
    Atom,
    Clock,
    Target,
    Zap,
    Globe
} from 'lucide-react';

const CleanBackground = () => {
    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none bg-gray-50">
            {/* Subtle geometric pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gray-300 rounded-full transform rotate-12"></div>
                <div className="absolute top-1/3 right-1/3 w-40 h-40 bg-gray-300 rounded-full transform -rotate-6"></div>
                <div className="absolute bottom-1/3 left-1/2 w-36 h-36 bg-gray-300 rounded-full transform rotate-3"></div>
                <div className="absolute top-1/2 right-1/4 w-28 h-28 bg-gray-300 rounded-full transform -rotate-12"></div>
            </div>
        </div>
    );
};

export default function Phase2() {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                delayChildren: 0.2,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    const phase2Initiatives = [
        {
            icon: Wind,
            title: "Wind Turbines",
            description: "We utilize wind farms to maximize efficiency and deliver renewable energy to communities worldwide.",
            color: "var(--cyber-blue)",
            borderColor: "border-blue-400"
        },
        {
            icon: Recycle,
            title: "Waste-to-Energy",
            description: "Transforming waste into a sustainable resource. Our projects convert organic and industrial waste into clean energy, reducing landfill reliance and greenhouse emissions.",
            color: "var(--eco-green)",
            borderColor: "border-green-400"
        },
        {
            icon: Flame,
            title: "Biogas Power Plants",
            description: "Pioneering bio-energy by capturing methane and biogas. These turbines convert organic matter into renewable power, supporting sustainable energy transitions for industries and homes.",
            color: "var(--warning)",
            borderColor: "border-orange-400"
        },
        {
            icon: Car,
            title: "EV Charging Stations",
            description: "Infrastructure for electric vehicles with smart grid integration and rapid charging capabilities",
            color: "var(--cyber-blue)",
            borderColor: "border-blue-400"
        },
        {
            icon: Battery,
            title: "Energy Storage",
            description: "Advanced storage solutions for stable energy supply with grid-scale capabilities",
            color: "var(--electric-purple)",
            borderColor: "border-purple-400"
        },
        {
            icon: Server,
            title: "Renewable as a Service",
            description: "Sustainable energy solutions for data centers with reliable, green power delivery",
            color: "var(--neon-green)",
            borderColor: "border-green-400"
        },
        {
            icon: Leaf,
            title: "Biogas Power Plants",
            description: "Leveraging organic waste to generate clean energy with zero-waste operations",
            color: "var(--eco-green)",
            borderColor: "border-green-400"
        },
        {
            icon: Atom,
            title: "Small Medium Reactors",
            description: "Compact nuclear reactors for localized energy needs with enhanced safety features",
            color: "var(--electric-purple)",
            borderColor: "border-purple-400"
        }
    ];

    return (
        <div className="min-h-screen relative bg-gray-50">
            <CleanBackground />

            <div className="relative z-10 container mx-auto px-4 py-20">
                {/* Header Section */}
                <motion.div
                    className="text-center mb-20"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <motion.div
                        className="flex items-center justify-center mb-8"
                        variants={itemVariants}
                    >
                        <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg">
                            <Target className="w-12 h-12 text-green-600" />
                        </div>
                    </motion.div>

                    <motion.h1
                        className="text-4xl md:text-5xl font-bold mb-6 text-gray-900"
                        variants={itemVariants}
                    >
                        Market <span className="text-green-600">Opportunity</span>
                    </motion.h1>

                    <motion.p
                        className="text-lg max-w-3xl mx-auto leading-relaxed text-gray-600"
                        variants={itemVariants}
                    >
                        Expanding our renewable energy portfolio with cutting-edge technologies and innovative solutions.
                        <br />
                        <span className="text-green-600 font-medium">
                            Phase 2 • Coming Soon • Innovation
                        </span>
                    </motion.p>
                </motion.div>

                {/* Coming Soon Banner */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                >
                    <div className="inline-flex items-center gap-3 px-8 py-4 bg-white rounded-2xl mb-8 shadow-lg border border-gray-200">
                        <Clock className="w-6 h-6 text-green-600" />
                        <span className="text-2xl font-bold text-green-600">
                            2nd Phase: Soon
                        </span>
                        <Clock className="w-6 h-6 text-green-600" />
                    </div>

                    <p className="text-lg max-w-3xl mx-auto text-gray-600">
                        We're preparing to launch our next generation of renewable energy solutions.
                        Stay tuned for these innovative technologies that will revolutionize clean energy investment.
                    </p>
                </motion.div>

                {/* Phase 2 Initiatives Grid */}
                <motion.div
                    className="mb-20"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                >
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {phase2Initiatives.map((initiative, index) => {
                            const Icon = initiative.icon;
                            return (
                                <motion.div
                                    key={index}
                                    className="bg-white p-6 rounded-2xl relative overflow-hidden group shadow-lg border border-gray-200"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.0 + index * 0.1, duration: 0.6 }}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                >
                                    {/* Coming Soon Overlay */}
                                    <div className="absolute top-4 right-4 z-20">
                                        <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-full">
                                            <div className="w-1.5 h-1.5 rounded-full animate-pulse bg-green-600"></div>
                                            <span className="text-xs text-green-600">Soon</span>
                                        </div>
                                    </div>

                                    {/* Icon */}
                                    <div className="flex items-center justify-center mb-6">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <Icon className="w-8 h-8 text-green-600" />
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="text-center">
                                        <h3 className="text-xl font-bold mb-4 text-gray-900">
                                            {initiative.title}
                                        </h3>
                                        <p className="text-sm leading-relaxed text-gray-600">
                                            {initiative.description}
                                        </p>
                                    </div>

                                    {/* Progress Indicator */}
                                    <div className="mt-6">
                                        <div className="flex justify-between text-xs mb-2">
                                            <span className="text-gray-600">Development Progress</span>
                                            <span className="text-green-600">In Progress</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="h-2 rounded-full transition-all duration-1000 bg-green-600"
                                                style={{
                                                    width: `${60 + (index * 5)}%`
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Technology Preview Section */}
                <motion.div
                    className="bg-white p-8 md:p-12 rounded-2xl mb-20 shadow-lg border border-gray-200"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.6, duration: 0.8 }}
                >
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                            Next Generation <span className="text-green-600">Technologies</span>
                        </h2>
                        <p className="text-lg max-w-3xl mx-auto text-gray-600">
                            Our Phase 2 initiatives represent the future of renewable energy, combining cutting-edge technology
                            with sustainable practices to create unprecedented investment opportunities.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Zap className="w-10 h-10 text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-gray-900">
                                Smart Grid Integration
                            </h3>
                            <p className="text-sm text-gray-600">
                                Advanced grid technologies for optimal energy distribution and storage management
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Globe className="w-10 h-10 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-gray-900">
                                Global Scalability
                            </h3>
                            <p className="text-sm text-gray-600">
                                Worldwide deployment capabilities with localized adaptation for maximum impact
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Atom className="w-10 h-10 text-purple-600" />
                            </div>
                            <h3 className="text-xl font-bold mb-4 text-gray-900">
                                Innovation Driven
                            </h3>
                            <p className="text-sm text-gray-600">
                                Research-backed solutions with continuous improvement and technological advancement
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Call to Action */}
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.8, duration: 0.8 }}
                >
                    <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg border border-gray-200">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                            Be the First to <span className="text-green-600">Invest</span>
                        </h2>
                        <p className="text-lg max-w-2xl mx-auto mb-8 text-gray-600">
                            Join our early access program and be among the first to invest in these revolutionary
                            Phase 2 renewable energy technologies.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <motion.button
                                className="bg-green-600 text-white px-8 py-4 rounded-lg flex items-center justify-center hover:bg-green-700 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Clock className="w-5 h-5 mr-2" />
                                Get Early Access
                            </motion.button>

                            <motion.button
                                className="bg-gray-100 text-gray-700 px-8 py-4 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Target className="w-5 h-5 mr-2" />
                                Learn More
                            </motion.button>
                        </div>

                        <div className="mt-8 flex justify-center items-center gap-2">
                            <div className="w-2 h-2 rounded-full animate-pulse bg-green-600"></div>
                            <span className="text-sm text-gray-600">
                                Phase 2 Launch: Q2 2024
                            </span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
